/**
 * Product builder — choice button column picker (pure logic + browser global).
 */
(function (global) {
  'use strict';

  var CANDIDATES_BY_COUNT = {
    1: [1],
    2: [2],
    3: [3],
    4: [4, 2],
    5: [5, 3],
    6: [6, 3, 2, 1],
    7: [4, 2, 1],
    8: [4, 3, 2, 1]
  };

  /**
   * @param {number} count - visible button count
   * @param {number} containerWidth - grid container width in px
   * @param {number} maxLabelWidth - widest intrinsic label width in px
   * @returns {number|null} column count, or null when layout override should not apply
   */
  function pickColumnCount(count, containerWidth, maxLabelWidth) {
    if (!count || count >= 9) return null;
    if (!containerWidth || containerWidth <= 0) return null;
    var candidates = CANDIDATES_BY_COUNT[count] || [];
    for (var i = 0; i < candidates.length; i++) {
      var cols = candidates[i];
      if (maxLabelWidth <= containerWidth / cols) return cols;
    }
    if (count <= 3) return count;
    return candidates[candidates.length - 1] || 1;
  }

  var api = { pickColumnCount: pickColumnCount, CANDIDATES_BY_COUNT: CANDIDATES_BY_COUNT };
  global.PBButtonLayout = api;



  function patchProductBuilder(pb) {
    if (!pb || pb._pbButtonLayoutPatchV1) return pb;
    Object.assign(pb, {
      measureBuilderBtnWidth: function(btn) {
        if(!btn) return 0;
        const cs = getComputedStyle(btn);
        if(!this._pbMeasureCanvas) this._pbMeasureCanvas = document.createElement('canvas');
        const ctx = this._pbMeasureCanvas.getContext('2d');
        const font = cs.font || [cs.fontStyle, cs.fontVariant, cs.fontWeight, cs.fontSize, cs.fontFamily].filter(Boolean).join(' ');
        ctx.font = font;
        const text = (btn.textContent || '').replace(/\s+/g, ' ').trim();
        const padX = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
        const borderX = (parseFloat(cs.borderLeftWidth) || 0) + (parseFloat(cs.borderRightWidth) || 0);
        return Math.ceil(ctx.measureText(text).width) + padX + borderX;
      },

      measureOptionCellWidth: function(cell) {
        if(!cell) return 0;
        const btn = cell.querySelector('.builder-btn');
        let width = btn ? this.measureBuilderBtnWidth(btn) : 0;
        const tag = cell.querySelector(':scope > .pb-option-tag');
        if(tag && tag.getAttribute('aria-hidden') !== 'true') {
          if(!this._pbMeasureCanvas) this._pbMeasureCanvas = document.createElement('canvas');
          const ctx = this._pbMeasureCanvas.getContext('2d');
          const tagCs = getComputedStyle(tag);
          const tagFont = tagCs.font || [tagCs.fontStyle, tagCs.fontVariant, tagCs.fontWeight, tagCs.fontSize, tagCs.fontFamily].filter(Boolean).join(' ');
          ctx.font = tagFont;
          const tagText = (tag.textContent || '').replace(/\s+/g, ' ').trim();
          width = Math.max(width, Math.ceil(ctx.measureText(tagText).width));
        }
        return width;
      },

      measureChoiceBtnWidth: function(btn) {
        const cell = btn && btn.closest ? btn.closest('.pb-option-cell') : null;
        if(cell) return this.measureOptionCellWidth(cell);
        return this.measureBuilderBtnWidth(btn);
      },

      _scheduleButtonLayoutRetry: function(rootSelector, attempt) {
        const key = rootSelector || '#main-flow-container';
        if(!this._pbLayoutRetryKeys) this._pbLayoutRetryKeys = {};
        if(this._pbLayoutRetryKeys[key]) return;
        this._pbLayoutRetryKeys[key] = true;
        const self = this;
        const maxAttempts = 5;
        const runAttempt = function(n) {
          requestAnimationFrame(function() {
            requestAnimationFrame(function() {
              self.applyButtonWrap(key);
              let needsAnother = false;
              const root = document.querySelector(key);
              if(root) {
                root.querySelectorAll('.btn-grid, .variant-slider-track').forEach(function(group) {
                  if(group.offsetParent === null) return;
                  const visibleBtns = group.querySelectorAll('.builder-btn');
                  let visibleCount = 0;
                  visibleBtns.forEach(function(btn) { if(btn.offsetParent !== null) visibleCount++; });
                  if(!visibleCount) return;
                  if(visibleCount >= 9) return;
                  if(!group.clientWidth || !group.classList.contains('pb-layout-override')) needsAnother = true;
                });
              }
              if(needsAnother && n < maxAttempts) runAttempt(n + 1);
              else self._pbLayoutRetryKeys[key] = false;
            });
          });
        };
        runAttempt(typeof attempt === 'number' ? attempt : 0);
      },

      bindButtonLayoutResizeObserver: function() {
        if(!window.ResizeObserver) return;
        if(this._pbBtnLayoutObserver) {
          this.rebindButtonLayoutResizeObserver();
          return;
        }
        const self = this;
        this._pbBtnLayoutObserver = new ResizeObserver(function() {
          if(self._pbBtnLayoutTimer) clearTimeout(self._pbBtnLayoutTimer);
          self._pbBtnLayoutTimer = setTimeout(function() {
            self._pbBtnLayoutTimer = null;
            if(!document.body.classList.contains('builder-open')) return;
            const s = window.builderState;
            const sel = s.mode === 'SUB' ? '#sub-flow-container' : '#main-flow-container';
            self.applyButtonWrap(sel);
          }, 50);
        });
        this.rebindButtonLayoutResizeObserver();
      },

      rebindButtonLayoutResizeObserver: function() {
        if(!this._pbBtnLayoutObserver) return;
        this._pbBtnLayoutObserver.disconnect();
        const selectors = [
          '#main-flow-container .pb-bottom-wrapper > .pb-step-viewport',
          '#sub-flow-container .pb-bottom-wrapper > .pb-step-viewport',
          '#sub-flow-container #sub-builder-controls'
        ];
        selectors.forEach(sel => {
          const el = document.querySelector(sel);
          if(el) this._pbBtnLayoutObserver.observe(el);
        });
      },

      unbindButtonLayoutResizeObserver: function() {
        if(this._pbBtnLayoutTimer) clearTimeout(this._pbBtnLayoutTimer);
        this._pbBtnLayoutTimer = null;
        if(!this._pbBtnLayoutObserver) return;
        this._pbBtnLayoutObserver.disconnect();
        this._pbBtnLayoutObserver = null;
      },

      applyButtonLayout: function(rootSelector) {
        const root = document.querySelector(rootSelector);
        if(!root) return;
        const pickColumnCount = (window.PBButtonLayout && window.PBButtonLayout.pickColumnCount) || null;
        const groups = root.querySelectorAll('.btn-grid, .variant-slider-track');
        let needsWidthRetry = false;
        groups.forEach(group => {
          const buttons = Array.from(group.querySelectorAll('.builder-btn')).filter(btn => btn.offsetParent !== null);
          const count = buttons.length;
          group.classList.remove('pb-layout-override');
          group.style.removeProperty('--pb-cols');
          group.style.removeProperty('--pb-gap');
          group.removeAttribute('data-btn-count');
          if(!count) return;
          const containerWidth = group.clientWidth;
          if(!containerWidth) {
            needsWidthRetry = true;
            return;
          }
          const maxTextWidth = Math.max(...buttons.map(btn => this.measureChoiceBtnWidth(btn)));
          const chosenCols = pickColumnCount ? pickColumnCount(count, containerWidth, maxTextWidth) : null;
          if(chosenCols == null) return;
          group.classList.add('pb-layout-override');
          group.style.setProperty('--pb-cols', String(chosenCols));
          group.style.setProperty('--pb-gap', '8px');
          group.setAttribute('data-btn-count', String(count));
        });
        if(needsWidthRetry) this._scheduleButtonLayoutRetry(rootSelector);
      },

      applyButtonSizing: function(rootSelector) {
        const root = document.querySelector(rootSelector);
        if(!root) return;
        this.applyButtonLayout(rootSelector);
        root.querySelectorAll('.btn-grid, .variant-slider-track').forEach(group => {
          const containerWidth = group.clientWidth || 0;
          const colWidth = group.classList.contains('pb-layout-override')
            ? (containerWidth - (parseFloat(group.style.getPropertyValue('--pb-gap') || '8') * (parseInt(group.style.getPropertyValue('--pb-cols') || '1', 10) - 1))) / parseInt(group.style.getPropertyValue('--pb-cols') || '1', 10)
            : containerWidth;
          group.querySelectorAll('.builder-btn').forEach(btn => {
            btn.classList.remove('pb-wrap-text');
            const labelWidth = this.measureChoiceBtnWidth(btn);
            if(colWidth && labelWidth > colWidth) btn.classList.add('pb-wrap-text');
          });
        });
        if(window.PBOptionLabel && window.PBOptionLabel.syncTagRowsInRoot) {
          window.PBOptionLabel.syncTagRowsInRoot(root);
        }
      },
      applyButtonWrap: function(rootSelector) {
        if (typeof this.isBuilderModalMeasurable !== 'function') return;
        if (!this.isBuilderModalMeasurable()) return;
        const root = document.querySelector(rootSelector);
        if(!root) return;
        if(window.PBOptionLabel) window.PBOptionLabel.enhance(root);
        if(typeof this.updateVariantPriceBadges === 'function') {
          this.updateVariantPriceBadges();
        } else {
          this.applyButtonSizing(rootSelector);
        }
      },
    });
    pb._pbButtonLayoutPatchV1 = 1;
    return pb;
  }

  api.patchProductBuilder = patchProductBuilder;

  function applyPatch() {
    patchProductBuilder(global.productBuilder);
  }
  if (global.document && global.document.readyState === 'loading') {
    global.document.addEventListener('DOMContentLoaded', applyPatch, { once: true });
  } else {
    applyPatch();
  }


  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
})(typeof window !== 'undefined' ? window : globalThis);
